<template>
  <ModalReleaseHighlights
    v-if="isModalMounted"
    v-model:visible="isVisible"
    v-model:has-opted-out="hasOptedOut"
    :releases="releases"
    :current-version="packageInfo.version"
    :last-known-version="shownLastKnownVersion"
    :media-base-url="RELEASE_HIGHLIGHTS_MEDIA_BASE_URL"
    :opt-out-hint="optOutHint"
    :all-releases-link="{
      name: ERouteName.RELEASE_HIGHLIGHTS,
    }"
    @close="onClose"
  />
</template>

<script setup lang="ts">
import {
  i18n,
} from '@i18n';
import type {
  IQTooltipOptions,
} from '@qmulus/ui';
import ModalReleaseHighlights from '@qmulus/ui/components/release-highlights/modal-release-highlights.vue';
import type {
  TRelease,
} from '@qmulus/ui/components/release-highlights/release-highlights.ts';
import type {
  TChangelogRelease,
} from '@qmulus/ui/utils/changelog.ts';
import {
  computed,
  shallowRef,
  watch,
} from 'vue';

import {
  buildReleaseHighlightsOptOutHint,
} from './modal-release-highlights-host.ts';

import packageInfo from '#/services/frontend/package.json';
import {
  loadChangelogReleases,
} from '@/release-highlights/changelog.ts';
import {
  RELEASE_HIGHLIGHTS_MEDIA_BASE_URL,
  getReleaseHighlightsStartupAction,
  getReleasesInLocale,
} from '@/release-highlights/release-highlights.ts';
import {
  RELEASE_HIGHLIGHTS,
} from '@/release-highlights/releases/index.ts';
import {
  ERouteName,
} from '@/router/route-name.ts';
import {
  useCustomUserStorageStore,
} from '@/store/pinia/custom-user-storage.ts';
import type {
  TSupportedLocale,
} from '@/types/i18n.ts';

const props = defineProps<{
  /**
   * Whether a user is signed in and their settings are loaded from the server. Settings cached in
   * the browser are not enough: saving the opt-out while the settings are still loading is dropped.
   */
  isEnabled: boolean,
}>();

const customUserStorageStore = useCustomUserStorageStore();

const changelogReleases = shallowRef<TChangelogRelease[]>([]);

const releases = computed<TRelease[]>(() => getReleasesInLocale({
  definitions: RELEASE_HIGHLIGHTS,
  locale: i18n.global.locale.value as TSupportedLocale,
  changelogReleases: changelogReleases.value,
}));

const optOutHint = computed<IQTooltipOptions>(() => buildReleaseHighlightsOptOutHint());

const isModalMounted = shallowRef(false);
const isVisible = shallowRef(false);
const hasOptedOut = shallowRef(false);
// Kept from the check, so that the modal keeps its releases after closing stores the current version.
// Missing for users without a last known version while release highlights are being introduced.
const shownLastKnownVersion = shallowRef<string>();

async function checkReleaseHighlights() {
  const releaseHighlightsSettings = await customUserStorageStore.loadReleaseHighlights();
  const action = getReleaseHighlightsStartupAction({
    releases: releases.value,
    currentVersion: packageInfo.version,
    lastKnownVersion: releaseHighlightsSettings.lastKnownVersion,
    hasOptedOut: customUserStorageStore.customUserSettings.hasOptedOutOfReleaseHighlights,
  });

  let exhaustiveCheck: never;
  switch (action) {
    case 'storeCurrentVersion':
      customUserStorageStore.createOrUpdateReleaseHighlights({
        lastKnownVersion: packageInfo.version,
      });
      break;
    case 'showModal':
      shownLastKnownVersion.value = releaseHighlightsSettings.lastKnownVersion;
      changelogReleases.value = await loadChangelogReleases();
      isModalMounted.value = true;
      isVisible.value = true;
      break;
    case 'none':
      break;
    default:
      exhaustiveCheck = action;
      throw new Error(`Unhandled case ${exhaustiveCheck}.`);
  }
}

let hasCheckedReleaseHighlights = false;
watch(
  () => props.isEnabled,
  (isEnabled) => {
    if (!isEnabled || hasCheckedReleaseHighlights) {
      return;
    }
    hasCheckedReleaseHighlights = true;
    checkReleaseHighlights();
  },
  {
    immediate: true,
  },
);

function onClose() {
  customUserStorageStore.createOrUpdateReleaseHighlights({
    lastKnownVersion: packageInfo.version,
  });
  if (hasOptedOut.value && !customUserStorageStore.customUserSettings.hasOptedOutOfReleaseHighlights) {
    customUserStorageStore.createOrUpdateCustomUserSettings({
      ...customUserStorageStore.customUserSettings,
      hasOptedOutOfReleaseHighlights: true,
    });
  }
}
</script>
